import React from 'react';
import {
  Clapperboard,
  Camera,
  Mic,
  Smartphone,
  Images,
  Video,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { DeliverableType } from '../../types';
import { useI18n } from '../../context/I18nContext';
import Tooltip from '../ui/Tooltip';

interface ProjectTypeIconProps {
  type?: DeliverableType | string | null;
  /** `avatar` = colored picker circle; `inline` = tinted icon next to titles (e.g. project card). */
  variant?: 'avatar' | 'inline';
  className?: string;
  avatarClassName?: string;
  showTooltip?: boolean;
}

const PROJECT_TYPE_I18N_KEYS: Record<DeliverableType, string> = {
  short_video: 'projects.typeShortVideo',
  photo: 'projects.typePhoto',
  voice_over: 'projects.typeVoiceOver',
  story: 'projects.typeStory',
  post: 'projects.typePost',
  long_video: 'projects.typeLongVideo',
  other: 'projects.typeOther',
};

/** Soft per-type tint for inline icons (card titles). */
const PROJECT_TYPE_INLINE_COLOR: Record<DeliverableType, string> = {
  short_video: 'text-violet-500',
  photo: 'text-sky-500',
  voice_over: 'text-amber-500',
  story: 'text-rose-500',
  post: 'text-teal-500',
  long_video: 'text-indigo-500',
  other: 'text-slate-500',
};

/** Avatar circle gradients for pickers — one hue family per type. */
const PROJECT_TYPE_AVATAR_BG: Record<DeliverableType, string> = {
  short_video: 'bg-gradient-to-br from-violet-500 to-fuchsia-500',
  photo: 'bg-gradient-to-br from-sky-500 to-blue-500',
  voice_over: 'bg-gradient-to-br from-amber-500 to-orange-500',
  story: 'bg-gradient-to-br from-rose-500 to-pink-500',
  post: 'bg-gradient-to-br from-teal-500 to-emerald-500',
  long_video: 'bg-gradient-to-br from-indigo-500 to-violet-500',
  other: 'bg-gradient-to-br from-slate-500 to-gray-500',
};

export function getProjectTypeLucideIcon(type?: string | null): LucideIcon {
  switch (type) {
    case 'short_video':
      return Clapperboard;
    case 'photo':
      return Camera;
    case 'voice_over':
      return Mic;
    case 'story':
      return Smartphone;
    case 'post':
      return Images;
    case 'long_video':
      return Video;
    case 'other':
    default:
      return FileText;
  }
}

/**
 * Icon for a project deliverable type — shared by ProjectSearch (avatar) and ProjectCard (inline).
 */
const ProjectTypeIcon: React.FC<ProjectTypeIconProps> = ({
  type,
  variant = 'avatar',
  className,
  avatarClassName,
  showTooltip = true,
}) => {
  const { t } = useI18n();
  const Icon = getProjectTypeLucideIcon(type);
  const key = (type as DeliverableType) in PROJECT_TYPE_I18N_KEYS ? (type as DeliverableType) : 'other';
  const label = PROJECT_TYPE_I18N_KEYS[key] ? t(PROJECT_TYPE_I18N_KEYS[key]) : '';

  const iconEl =
    variant === 'inline' ? (
      <Icon
        className={
          className ?? `w-4 h-4 shrink-0 ${PROJECT_TYPE_INLINE_COLOR[key]}`
        }
        aria-hidden={!label}
      />
    ) : (
      <div
        className={
          avatarClassName ??
          `w-8 h-8 ${PROJECT_TYPE_AVATAR_BG[key]} rounded-full flex items-center justify-center flex-shrink-0`
        }
        aria-hidden={!label}
      >
        <Icon className={className ?? 'w-4 h-4 text-white'} />
      </div>
    );

  if (showTooltip && label) {
    return <Tooltip content={label}>{iconEl}</Tooltip>;
  }

  return iconEl;
};

export default ProjectTypeIcon;
